import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../../api/api';
import { useInRouterContext, useSearchParams } from 'react-router-dom';
import useAdminDraftGuard from '../useAdminDraftGuard';
import { useUserStore } from '../../../stores/userStore';
import { popupHttps, popupLocalDate, popupDates, popupRecord } from './popupAdminFields';
import { objectStorageApi } from '../../../api/objectStorage';

const endpoint = '/admin/ads/startup-popup/items';
const emptyForm = () => ({
  title: '',
  imageUrl: '',
  destinationUrl: '',
  enabled: false,
  showAdMark: true,
  startsAt: '',
  endsAt: '',
  actionText: '',
  layout: '',
});
const localDate = popupLocalDate;

function RoutedStartup({ account }) {
  const [params, setParams] = useSearchParams();
  const raw = params.get('popupLayout');
  const filter = ['CENTER', 'BOTTOM'].includes(raw) ? raw : 'ALL';
  return (
    <StartupSession
      key={account}
      filter={filter}
      setFilter={(value) => {
        const next = new URLSearchParams(params);
        if (value === 'ALL') next.delete('popupLayout');
        else next.set('popupLayout', value);
        setParams(next, { replace: true });
      }}
    />
  );
}
function LocalStartup() {
  const [filter, setFilter] = useState('ALL');
  return <StartupSession filter={filter} setFilter={setFilter} />;
}
export default function StartupPopupManagement() {
  const user = useUserStore((state) => state.user);
  const account = JSON.stringify([user?.id, user?.role]);
  const routed = useInRouterContext();
  return routed ? <RoutedStartup account={account} /> : <LocalStartup key={account} />;
}
function StartupSession({ filter, setFilter }) {
  const [form, setForm] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [notice, setNotice] = useState('');
  const pending = useRef(false);
  const life = useRef({ live: true, read: 0 });
  const original = useRef(null);
  useEffect(() => {
    const session = life.current;
    session.live = true;
    return () => {
      session.live = false;
      session.read += 1;
    };
  }, []);
  const draft = useAdminDraftGuard({ form, file }, { enabled: form !== null || busy, busy });
  const listHeading = useRef(null);
  const layout = form?.layout;
  const load = async () => {
    const session = life.current,
      read = ++session.read;
    const current = () => session.live && read === session.read;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.getAuth(endpoint);
      if (!current()) return;
      if (!Array.isArray(data.data)) throw new Error();
      setItems(
        data.data.map((item) => ({
          ...popupRecord(item),
          actionText: item.actionText || '',
          destinationUrl: item.destinationUrl || '',
          imageUrl: item.imageUrl || '',
        })),
      );
    } catch {
      if (current()) setError('팝업 설정을 불러오지 못했습니다.');
    } finally {
      if (current()) setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  useEffect(() => {
    if (!file) {
      setPreview('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const change = (name, value) => {
    if (pending.current) return;
    setSaved(false);
    setForm((current) => ({ ...current, [name]: value }));
  };
  const edit = (item) => {
    if (pending.current || loading || !draft.confirmDiscard()) return;
    original.current = item;
    setNotice('');
    draft.resetBaseline();
    setForm(
      item
        ? {
            ...item,
            layout: item.layout || 'CENTER',
            startsAt: localDate(item.startsAt),
            endsAt: localDate(item.endsAt),
          }
        : emptyForm(),
    );
    setFile(null);
    setError('');
    setSaved(false);
  };
  const save = async (event) => {
    event.preventDefault();
    if (pending.current || !form) return;
    pending.current = true;
    const session = life.current;
    session.read += 1;
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      for (const key of ['startsAt', 'endsAt']) localDate(form[key]);
      const payload = {
        ...form,
        startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
        endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
      };
      delete payload.id;
      for (const key of ['startsAt', 'endsAt'])
        if (original.current && form[key] === localDate(original.current[key]))
          payload[key] = original.current[key] || null;
      payload.title = payload.title.trim();
      if (!payload.title || payload.title.length > 160 || (payload.actionText || '').length > 60)
        throw new Error('팝업 설명은 1~160자, 기존 버튼 문구는 60자 이하입니다.');
      popupDates(payload);
      if (!file && !popupHttps(payload.imageUrl))
        throw new Error('팝업 이미지는 HTTPS 주소여야 합니다.');
      if (!['CENTER', 'BOTTOM'].includes(payload.layout))
        throw new Error('팝업 형식을 먼저 선택해주세요.');
      if (payload.startsAt && payload.endsAt && payload.startsAt >= payload.endsAt)
        throw new Error('종료 시각은 시작 시각보다 늦어야 합니다.');
      if (!popupHttps(payload.destinationUrl))
        throw new Error('랜딩 URL은 인증 정보 없는 HTTPS 주소여야 합니다.');
      let replaceOther = false;
      if (payload.enabled) {
        const latest = await api.getAuth(endpoint);
        if (!session.live) return;
        if (!Array.isArray(latest.data.data))
          throw new Error('공개 팝업 목록을 확인하지 못했습니다.');
        const conflicts = latest.data.data.filter(
          (item) => item.enabled && (item.layout || 'CENTER') !== payload.layout,
        );
        if (conflicts.length) {
          const other = payload.layout === 'CENTER' ? '하단형' : '중앙형';
          if (
            !window.confirm(
              `${other} 광고 ${conflicts.length}개가 공개 또는 예약 중입니다. 중단하고 이 팝업을 공개할까요? 기존 광고 내용은 보존됩니다.`,
            )
          )
            return;
          replaceOther = true;
        }
      }
      if (file) {
        const body = new FormData();
        body.append('image', file);
        const response = await objectStorageApi.uploadImage(body);
        if (!session.live) return;
        if (!popupHttps(response.data?.url || ''))
          throw new Error('이미지 업로드 결과가 올바른 HTTPS 주소가 아닙니다.');
        payload.imageUrl = response.data.url;
        setForm((current) => ({ ...current, imageUrl: payload.imageUrl }));
        setFile(null);
      }
      if (!payload.imageUrl) throw new Error('팝업 이미지를 업로드해주세요.');
      if (!session.live) return;
      const suffix = replaceOther ? '?replaceOther=true' : '';
      const response = form.id
        ? await api.putAuth(`${endpoint}/${form.id}${suffix}`, payload)
        : await api.postAuth(`${endpoint}${suffix}`, payload);
      if (!session.live) return;
      const confirmed = response?.data?.data
        ? popupRecord(response.data.data)
        : form.id
          ? { ...payload, id: form.id }
          : null;
      if (confirmed && !confirmed.id)
        throw new Error('저장 결과를 확인하지 못했습니다. 다시 불러와주세요.');
      if (confirmed)
        setItems((current) => {
          const items = replaceOther
            ? current.map((item) =>
                (item.layout || 'CENTER') !== payload.layout ? { ...item, enabled: false } : item,
              )
            : current;
          return form.id
            ? items.map((item) => (item.id === form.id ? confirmed : item))
            : [...items, confirmed];
        });
      draft.markClean();
      setForm(null);
      setFilter('ALL');
      setFile(null);
      setSaved(true);
      await load();
      if (session.live) {
        listHeading.current?.focus();
        listHeading.current?.scrollIntoView?.({ block: 'start' });
      }
    } catch (failure) {
      if (session.live)
        setError(failure.response?.data?.msg || failure.message || '저장하지 못했습니다.');
    } finally {
      pending.current = false;
      if (session.live) setBusy(false);
    }
  };
  const remove = async (item) => {
    if (pending.current || loading || !draft.confirmDiscard()) return;
    if (
      !window.confirm(
        `‘${item.title}’ 팝업을 삭제할까요? 삭제한 설정은 복구할 수 없으며 새로 접속하는 앱의 광고 목록에서 제외됩니다. 다른 팝업과 공용 이미지는 삭제되지 않습니다.`,
      )
    )
      return;
    pending.current = true;
    const session = life.current;
    session.read += 1;
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      await api.deleteAuth(`${endpoint}/${item.id}`);
      if (!session.live) return;
      setItems((current) => current.filter((value) => value.id !== item.id));
      draft.markClean();
      setNotice('팝업을 삭제했습니다.');
      setForm(null);
      setFile(null);
      await load();
    } catch (failure) {
      if (session.live)
        setError(failure.response?.data?.msg || '삭제하지 못했습니다. 다시 시도해주세요.');
    } finally {
      pending.current = false;
      if (session.live) setBusy(false);
    }
  };
  return (
    <Stack spacing={2} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
      <Typography ref={listHeading} tabIndex={-1} variant="h6">
        앱 첫 접속 팝업
      </Typography>
      <Typography>
        팝업마다 중앙형 또는 하단형을 선택해 관리합니다. 같은 형식의 공개 팝업을 등록순으로 좌우
        넘겨 봅니다. 팝업 묶음은 완전 실행당 1회, 오늘 그만 보기는 모든 팝업에 기기의 자정까지
        적용됩니다.
      </Typography>
      {error && (
        <Alert
          severity="error"
          action={
            !form ? (
              <Button
                disabled={busy || loading}
                onClick={() => {
                  if (!pending.current) void load();
                }}
              >
                재시도
              </Button>
            ) : null
          }
        >
          {error}
        </Alert>
      )}
      {notice && <Alert severity="success">{notice}</Alert>}
      {saved && (
        <Alert severity="success">
          저장했습니다. 앱을 완전히 종료 후 다시 실행해 확인하세요. 오늘 그만 보기를 선택한 기기는
          당일 표시되지 않습니다.
        </Alert>
      )}
      {!form ? (
        <Stack spacing={2}>
          <Button variant="contained" disabled={loading || busy} onClick={() => edit(null)}>
            팝업 등록
          </Button>
          <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
            {[
              ['ALL', '전체'],
              ['CENTER', '중앙형'],
              ['BOTTOM', '하단형'],
            ].map(([key, label]) => (
              <Button
                key={key}
                variant={filter === key ? 'contained' : 'outlined'}
                disabled={busy || loading}
                onClick={() => setFilter(key)}
              >
                {label}
              </Button>
            ))}
          </Stack>
          {loading ? (
            <Typography>설정을 불러오는 중…</Typography>
          ) : !error && !items.length ? (
            <Typography>등록된 팝업이 없습니다.</Typography>
          ) : null}
          {items
            .filter((item) => filter === 'ALL' || (item.layout || 'CENTER') === filter)
            .map((item) => (
              <Stack
                key={item.id}
                direction="row"
                useFlexGap
                flexWrap="wrap"
                spacing={2}
                alignItems="center"
              >
                <Box
                  component="img"
                  src={item.imageUrl}
                  alt=""
                  sx={{
                    width: 72,
                    maxWidth: '100%',
                    flexShrink: 0,
                    aspectRatio: item.layout === 'BOTTOM' ? '9 / 8' : '4 / 5',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }}
                />
                <Box
                  sx={{
                    flex: '1 0 140px',
                    minWidth: 0,
                    maxWidth: '100%',
                    overflowWrap: 'anywhere',
                  }}
                >
                  <Typography>{item.title}</Typography>
                  <Typography variant="caption">
                    {item.layout === 'BOTTOM' ? '하단형 · 9:8' : '중앙형 · 4:5'} ·{' '}
                    {item.enabled ? '공개 설정' : '비공개'} ·{' '}
                    {item.showAdMark ? 'AD 표시' : 'AD 미표기'}
                  </Typography>
                </Box>
                <Button disabled={loading || busy} onClick={() => edit(item)}>
                  수정
                </Button>
                <Button color="error" disabled={loading || busy} onClick={() => remove(item)}>
                  삭제
                </Button>
              </Stack>
            ))}
        </Stack>
      ) : (
        <Box component="form" aria-label="첫 접속 팝업 편집" onSubmit={save}>
          <Stack
            spacing={2}
            component="fieldset"
            disabled={busy}
            sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
          >
            <TextField
              autoFocus
              disabled={busy}
              select
              required
              label="팝업 형식"
              value={form.layout}
              onChange={(e) => change('layout', e.target.value)}
              helperText="다른 형식을 공개하면 기존 형식의 공개·예약 광고를 중단할지 확인합니다."
            >
              <MenuItem value="" disabled>
                형식을 선택해주세요
              </MenuItem>
              <MenuItem value="CENTER">중앙형 · 세로 4:5</MenuItem>
              <MenuItem value="BOTTOM">하단형 · 전체 폭 9:8</MenuItem>
            </TextField>
            <TextField
              required
              label="팝업 설명 (관리·화면 읽기용)"
              value={form.title}
              inputProps={{ maxLength: 160 }}
              onChange={(e) => change('title', e.target.value)}
              helperText="이미지에는 표시되지 않습니다. 목록에서 구분하고, VoiceOver 등이 이미지 속 내용을 읽는 데 사용합니다. 예: 9월 음감회 신청 안내"
            />
            <Button component="label" disabled={busy} variant="outlined">
              이미지 선택
              <input
                hidden
                aria-label="팝업 이미지 파일"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => {
                  if (pending.current) return;
                  const next = e.target.files?.[0];
                  e.target.value = '';
                  if (!next) return;
                  if (
                    !['image/png', 'image/jpeg', 'image/webp'].includes(next.type) ||
                    next.size > 5 * 1024 * 1024
                  ) {
                    setError('PNG/JPG/WebP 5MB 이하 이미지를 선택해주세요.');
                    return;
                  }
                  setFile(next);
                  setError('');
                  setSaved(false);
                }}
              />
            </Button>
            <Typography variant="caption">
              {layout === 'BOTTOM' ? (
                '하단형 9:8 · 권장 1080×960px · 화면 전체 폭 · 우측 상단 페이지 표시와 수동 스와이프. 별도 하단 버튼 없이 이미지 전체가 랜딩 링크입니다. 기존 4:3 등 다른 비율은 중앙 크롭되므로 미리보기를 확인해주세요. 하단 40px에는 중요한 문구를 피해주세요.'
              ) : (
                <>
                  고정 4:5 세로 이미지 영역 · 권장 1080×1350px · PNG/JPG/WebP 5MB 이하. 앱 폭은
                  화면의 71% 이내(최대 360pt)입니다. 이미지는 중앙 기준으로 영역을 꽉 채우며, 다른
                  비율은 가장자리가 일부 잘립니다. 넘길 때 높이는 바뀌지 않습니다.
                </>
              )}
            </Typography>
            <TextField
              required
              label="외부 랜딩 URL"
              type="url"
              value={form.destinationUrl}
              inputProps={{ maxLength: 2048 }}
              onChange={(e) => change('destinationUrl', e.target.value)}
            />
            <FormControlLabel
              label="AD 표시"
              control={
                <Checkbox
                  checked={form.showAdMark}
                  onChange={(e) => change('showAdMark', e.target.checked)}
                />
              }
            />
            <TextField
              label="공개 시작 (선택, 관리자 기기 시간)"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={form.startsAt}
              onChange={(e) => change('startsAt', e.target.value)}
            />
            <TextField
              label="공개 종료 (선택, 관리자 기기 시간)"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={form.endsAt}
              onChange={(e) => change('endsAt', e.target.value)}
            />
            {(preview || form.imageUrl) && (
              <Box
                sx={{
                  maxWidth: 320,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'rgba(0,0,0,0.55)',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    ...(layout === 'BOTTOM'
                      ? { borderRadius: '20px 20px 0 0', overflow: 'hidden' }
                      : {}),
                  }}
                >
                  <Box
                    component="img"
                    alt="팝업 미리보기"
                    src={preview || form.imageUrl}
                    sx={{
                      width: '100%',
                      aspectRatio: layout === 'BOTTOM' ? '9 / 8' : '4 / 5',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block',
                    }}
                  />
                  {form.showAdMark && (
                    <Box
                      sx={{
                        position: 'absolute',
                        ...(layout === 'BOTTOM' ? { bottom: 42, right: 16 } : { top: 8, right: 8 }),
                        color: 'rgba(255,255,255,0.82)',
                        bgcolor: 'rgba(8,8,8,0.12)',
                        fontSize: 10,
                        lineHeight: '13px',
                        px: '5px',
                        fontWeight: 600,
                        py: '1px',
                        borderRadius: '8px',
                      }}
                    >
                      광고
                    </Box>
                  )}
                  {layout === 'BOTTOM' && (
                    <Box
                      aria-label="페이지 표시 예시"
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 16,
                        bgcolor: 'rgba(0,0,0,0.35)',
                        color: '#fff',
                        borderRadius: '20px',
                        px: '10px',
                        py: '5px',
                        fontSize: 14,
                        lineHeight: '18px',
                        fontWeight: 600,
                      }}
                    >
                      1
                      <Box component="span" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        /5
                      </Box>
                    </Box>
                  )}
                </Box>
                <Stack
                  direction="row"
                  sx={{
                    minHeight: 48,
                    flexWrap: 'wrap',
                    gap: 1,
                    px: layout === 'BOTTOM' ? '20px' : '12px',
                    boxSizing: 'border-box',
                    pt: layout === 'BOTTOM' ? 0 : '8px',
                    pb: layout === 'BOTTOM' ? '8px' : 0,
                    order: layout === 'BOTTOM' ? -1 : undefined,
                    justifyContent: 'space-between',
                    alignItems: layout === 'BOTTOM' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <Typography
                    sx={
                      layout === 'BOTTOM'
                        ? { fontSize: 16, lineHeight: '22px', color: '#fff' }
                        : { fontSize: 14, lineHeight: '20px', color: '#fff' }
                    }
                  >
                    오늘 그만 보기
                  </Typography>
                  <Typography
                    sx={
                      layout === 'BOTTOM'
                        ? { fontSize: 16, lineHeight: '22px', color: '#fff' }
                        : { fontSize: 14, lineHeight: '20px', color: '#fff' }
                    }
                  >
                    닫기 ×
                  </Typography>
                </Stack>
              </Box>
            )}
            {layout === 'BOTTOM' && (
              <Typography variant="caption">
                페이지 표시는 여러 광고가 있을 때만 표시됩니다. 위 1/5는 예시입니다. 구형 앱은 이전
                레이아웃으로 표시될 수 있습니다.
              </Typography>
            )}
            <FormControlLabel
              label="팝업 공개"
              control={
                <Checkbox
                  checked={form.enabled}
                  onChange={(e) => change('enabled', e.target.checked)}
                />
              }
            />
            <Button variant="contained" type="submit">
              {busy ? '저장 중…' : '저장'}
            </Button>
            <Button
              onClick={() => {
                if (pending.current || !draft.confirmDiscard()) return;
                setForm(null);
                setFile(null);
                setError('');
                requestAnimationFrame(() => listHeading.current?.focus());
              }}
            >
              취소
            </Button>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
